import { NULL_FILTER_VALUE } from '@gearbelt/global';
import { queryOptions } from '@tanstack/react-query';

export const columnFilterKeys = {
  all: ['column-filter'] as const,
  byColumn: (columnId: string) => [...columnFilterKeys.all, 'options', columnId] as const,
  options: (columnId: string, filterValue: string | string[] | [Date, Date] | undefined) => [...columnFilterKeys.byColumn(columnId), filterValue] as const,
};

export type ColumnFilterState = { id: string; value: unknown };

export interface BuildFilterFieldsOptions {
  /** Transform a column's raw filter value before the null-sentinel split (per-id defaults, numeric-string strips). */
  transformValue?: (columnId: string, rawValue: unknown) => unknown;
  /** Static field entries merged after the map — never null-split (e.g. a pinned assetId, formId, or formFields array). */
  extraFields?: Record<string, unknown>;
  /** Drop mapped entries whose value is undefined before appending nullableFields. */
  dropUndefined?: boolean;
}

/**
 * Builds an API `filters.fields` object from column-filter state, stripping the
 * `NULL_FILTER_VALUE` sentinel out of each field's value array and collecting the
 * affected field keys into `nullableFields` (which the API maps to IS NULL conditions).
 */
export function buildFilterFields(
  filters: ColumnFilterState[] | undefined,
  filterFieldMap: Record<string, string>,
  options: BuildFilterFieldsOptions = {},
): { fields: Record<string, unknown> & { nullableFields?: string[] } } {
  const { transformValue, extraFields, dropUndefined } = options;
  const nullableFields: string[] = [];

  const mappedEntries = Object.entries(filterFieldMap).map(([columnId, fieldKey]) => {
    const rawValue = (filters ?? []).find((filter) => filter.id === columnId)?.value;
    const value = transformValue ? transformValue(columnId, rawValue) : rawValue;

    if (Array.isArray(value) && value.includes(NULL_FILTER_VALUE)) {
      nullableFields.push(fieldKey);
      const stripped = value.filter((entry) => entry !== NULL_FILTER_VALUE);
      return [fieldKey, stripped.length ? stripped : undefined] as const;
    }
    return [fieldKey, value] as const;
  });

  const keptEntries = dropUndefined ? mappedEntries.filter(([, value]) => value !== undefined) : mappedEntries;

  const fields: Record<string, unknown> = {
    ...Object.fromEntries(keptEntries),
    ...extraFields,
  };

  return { fields: { ...fields, nullableFields: nullableFields.length ? nullableFields : undefined } };
}

export const getColumnFilterOptionsQueryOptions = ({
  columnId,
  filterValue,
  getFilterOptions,
}: {
  columnId: string;
  filterValue: string | string[] | [Date, Date] | undefined;
  getFilterOptions?: (inputValue?: string | undefined, value?: string | string[] | undefined) => Promise<{ label: string; value: string }[]>;
}) => {
  return queryOptions({
    queryKey: columnFilterKeys.options(columnId, filterValue),
    queryFn: async () => {
      const stringValue = Array.isArray(filterValue)
        ? filterValue.every((v): v is string => typeof v === 'string')
          ? filterValue
          : undefined
        : typeof filterValue === 'string'
          ? [filterValue]
          : undefined;
      // The null-filter sentinel isn't a real entity id — the popover renders its
      // own option for it. Strip it before hydrating labels so it never reaches an
      // id-filtered lookup (e.g. GetUsers) and fails UUID validation.
      const idsToHydrate = stringValue?.filter((value) => value !== NULL_FILTER_VALUE);
      const result = await getFilterOptions?.(undefined, idsToHydrate);
      return result?.map((option) => ({ label: option.label, value: Array.isArray(option.value) ? option.value[0] : option.value }));
    },
    gcTime: 0,
    staleTime: 0,
    enabled: !!getFilterOptions,
  });
};
